/**
 * Pagina /leave — gestione richieste ferie/permessi.
 *
 * Tabs:
 *  - "Da approvare" → solo per chi ha leave.approve (AM/OPS)
 *  - "Tutte"        → richieste in scope (OPS=tutti, AM=propri store, SM=proprio store)
 *  - "Le mie"       → richieste personali del dipendente loggato
 *
 * Componenti:
 *  - LeaveRequestRow: una richiesta inline con stato/azioni
 *  - NewRequestDialog: form creazione richiesta (self o per terzi)
 *  - DecisionDialog: approve/reject con note
 */
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { usePermissions } from '../lib/usePermissions';
import { leaveApi, staffApi, storesApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import {
  CalendarDays, Plus, Check, X as XIcon, Clock, AlertCircle, Hourglass, Ban,
  CheckCircle2, XCircle
} from 'lucide-react';
import { LeaveCalendar } from '../components/LeaveCalendar';
import { StoreCascadeFilter, computeAllowedStoreIds, emptyCascade } from '../components/StoreCascadeFilter';

const TYPE_LABELS = {
  ferie: 'Ferie',
  permesso: 'Permesso ROL',
  malattia: 'Malattia',
  aspettativa: 'Aspettativa',
  lutto: 'Permesso lutto',
};

const STATUS_BADGE = {
  pending:   { label: 'In attesa',  cls: 'bg-amber-50 text-amber-800 border-amber-300', Icon: Hourglass },
  approved:  { label: 'Approvata',  cls: 'bg-emerald-50 text-emerald-800 border-emerald-300', Icon: CheckCircle2 },
  rejected:  { label: 'Rifiutata',  cls: 'bg-red-50 text-red-800 border-red-300', Icon: XCircle },
  cancelled: { label: 'Annullata',  cls: 'bg-slate-100 text-slate-600 border-slate-300', Icon: Ban },
};

function formatPeriod(req) {
  if (req.unit === 'hours') {
    const tw = req.start_time && req.end_time ? ` (${req.start_time}–${req.end_time})` : '';
    return `${req.start_date}${tw} · ${req.duration_hours}h`;
  }
  if (req.end_date && req.end_date !== req.start_date) {
    return `${req.start_date} → ${req.end_date} · ${req.duration_days} gg`;
  }
  return `${req.start_date} · 1 gg`;
}


function LeaveRequestRow({ req, isApprover, isOwner, onDecide, onCancel }) {
  const badge = STATUS_BADGE[req.status] || STATUS_BADGE.pending;
  const Icon = badge.Icon;
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-3 md:p-4 flex items-start gap-3 flex-wrap md:flex-nowrap" data-testid={`leave-row-${req.request_id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <p className="font-medium text-slate-900">{req.staff_display_name || req.staff_id}</p>
          <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 border rounded-sm bg-slate-50 text-slate-700 border-slate-300">
            {TYPE_LABELS[req.type] || req.type}
          </span>
          <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 border rounded-sm inline-flex items-center gap-1 ${badge.cls}`}>
            <Icon className="w-2.5 h-2.5" /> {badge.label}
          </span>
        </div>
        <p className="text-sm text-slate-700 flex items-center gap-1.5">
          <CalendarDays className="w-3.5 h-3.5 text-slate-400" /> {formatPeriod(req)}
        </p>
        {req.reason && <p className="text-xs text-slate-600 mt-1.5">{req.reason}</p>}
        {req.decision_reason && (
          <p className={`text-xs mt-1.5 italic ${req.status === 'rejected' ? 'text-red-700' : 'text-emerald-700'}`}>
            Nota responsabile: {req.decision_reason}
          </p>
        )}
        <p className="text-[11px] text-slate-400 mt-1">
          Inviata {new Date(req.created_at).toLocaleDateString('it-IT')}
          {req.decided_by_email && req.status !== 'pending' && (
            <> · Decisa da <b>{req.decided_by_email}</b> il {new Date(req.decided_at).toLocaleDateString('it-IT')}</>
          )}
        </p>
      </div>
      <div className="flex gap-1 ml-auto">
        {req.status === 'pending' && isApprover && (
          <>
            <Button variant="outline" size="sm" onClick={() => onDecide(req, 'approved')} className="gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`leave-approve-${req.request_id}`}>
              <Check className="w-3.5 h-3.5" /> <span className="hidden md:inline">Approva</span>
            </Button>
            <Button variant="outline" size="sm" onClick={() => onDecide(req, 'rejected')} className="gap-1 border-red-300 text-red-700 hover:bg-red-50" data-testid={`leave-reject-${req.request_id}`}>
              <XIcon className="w-3.5 h-3.5" /> <span className="hidden md:inline">Rifiuta</span>
            </Button>
          </>
        )}
        {req.status === 'pending' && isOwner && !isApprover && (
          <Button variant="ghost" size="sm" onClick={() => onCancel(req)} className="gap-1 text-slate-600" data-testid={`leave-cancel-${req.request_id}`}>
            <Ban className="w-3.5 h-3.5" /> Annulla
          </Button>
        )}
      </div>
    </div>
  );
}


function NewRequestDialog({ onClose, onCreated, staffList, defaultStaffId, canPickStaff }) {
  const [staffId, setStaffId] = useState(defaultStaffId || (staffList[0]?.staff_id || ''));
  const [type, setType] = useState('ferie');
  const [unit, setUnit] = useState('days');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('13:00');
  const [durationHours, setDurationHours] = useState(4);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  // Caso limite: l'utente non ha nessuno staff linkato e non ha permessi per
  // selezionarne uno dalla lista. Esempio: un OPS appena invitato che vuole
  // chiedere ferie per sé ma non ha ancora una scheda Staff. Mostra un
  // messaggio chiaro invece di un form non funzionante.
  const cannotProceed = !canPickStaff && !defaultStaffId;

  // Auto-fallback unità (permesso → ore, altri → giorni) gestito direttamente
  // nell'onChange del Select per evitare set-state-in-effect.
  const handleTypeChange = (t) => {
    setType(t);
    if (t === 'permesso') setUnit('hours');
    else if (t === 'ferie' || t === 'malattia' || t === 'lutto') setUnit('days');
  };

  async function submit() {
    if (!startDate) { toast.error('Data inizio obbligatoria'); return; }
    if (canPickStaff && !staffId) { toast.error('Seleziona uno staff'); return; }
    const payload = {
      type, unit,
      start_date: startDate,
      end_date: endDate || startDate,
      reason: reason.trim() || undefined,
    };
    if (canPickStaff) payload.staff_id = staffId;
    if (unit === 'hours') {
      payload.start_time = startTime;
      payload.end_time = endTime;
      payload.duration_hours = Number(durationHours);
      // Per ore, end_date = start_date
      payload.end_date = startDate;
    }
    setSaving(true);
    try {
      await leaveApi.create(payload);
      toast.success('Richiesta inviata');
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore invio richiesta');
    } finally { setSaving(false); }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="new-leave-dialog">
        <DialogHeader>
          <DialogTitle>Nuova richiesta</DialogTitle>
          <DialogDescription>
            {cannotProceed
              ? 'Profilo Staff non collegato al tuo account.'
              : 'Compila i campi per inviare una richiesta di ferie, permesso o assenza.'}
          </DialogDescription>
        </DialogHeader>
        {cannotProceed ? (
          <div className="py-4 space-y-3" data-testid="no-staff-warning">
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
              <p className="text-sm text-amber-900">
                Per richiedere ferie o permessi devi avere una scheda Staff collegata al tuo account.
              </p>
              <p className="text-xs text-amber-700 mt-1.5">
                Chiedi all&apos;Operations Manager (o HR Manager) di aprire la tua scheda dal modulo <b>Staff</b> e collegarla al tuo profilo utente.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Chiudi</Button>
            </DialogFooter>
          </div>
        ) : (
        <>
        <div className="space-y-3 py-2">
          {canPickStaff && (
            <div>
              <Label className="text-xs uppercase tracking-wide">Dipendente *</Label>
              <Select value={staffId} onValueChange={setStaffId}>
                <SelectTrigger data-testid="new-leave-staff"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {staffList.map(s => (
                    <SelectItem key={s.staff_id} value={s.staff_id}>
                      {s.display_name || `${s.first_name} ${s.last_name}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide">Tipo *</Label>
              <Select value={type} onValueChange={handleTypeChange}>
                <SelectTrigger data-testid="new-leave-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">Unità *</Label>
              <Select value={unit} onValueChange={setUnit}>
                <SelectTrigger data-testid="new-leave-unit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="days">Giorni</SelectItem>
                  <SelectItem value="hours">Ore</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide">Data inizio *</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="new-leave-start" />
            </div>
            {unit === 'days' && (
              <div>
                <Label className="text-xs uppercase tracking-wide">Data fine</Label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="new-leave-end" />
                <p className="text-[10px] text-slate-400 mt-1">Lascia vuoto se 1 giorno solo</p>
              </div>
            )}
          </div>
          {unit === 'hours' && (
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wide">Dalle</Label>
                <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide">Alle</Label>
                <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide">Ore</Label>
                <Input type="number" min="0.5" step="0.5" max="24" value={durationHours} onChange={(e) => setDurationHours(e.target.value)} data-testid="new-leave-hours" />
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-wide">Motivazione (opzionale)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Es. Vacanze estive con famiglia" data-testid="new-leave-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Annulla</Button>
          <Button onClick={submit} disabled={saving || !startDate} data-testid="new-leave-submit">{saving ? 'Invio…' : 'Invia richiesta'}</Button>
        </DialogFooter>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}


function DecisionDialog({ req, decision, onClose, onDecided }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  if (!req) return null;
  const isApprove = decision === 'approved';

  async function submit() {
    setSaving(true);
    try {
      await leaveApi.decide(req.request_id, decision, reason.trim() || undefined);
      toast.success(isApprove ? 'Richiesta approvata' : 'Richiesta rifiutata');
      onDecided();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore decisione');
    } finally { setSaving(false); }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="decision-dialog">
        <DialogHeader>
          <DialogTitle>{isApprove ? 'Approva richiesta' : 'Rifiuta richiesta'}</DialogTitle>
          <DialogDescription>
            <b>{req.staff_display_name}</b> · {TYPE_LABELS[req.type]} · {formatPeriod(req)}
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Label className="text-xs uppercase tracking-wide">Nota (opzionale)</Label>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                    placeholder={isApprove ? 'Es. OK, buona vacanza' : 'Es. Periodo coperto da altro collega'}
                    data-testid="decision-reason" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Annulla</Button>
          <Button onClick={submit} disabled={saving}
                  className={isApprove ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
                  data-testid="decision-submit">
            {saving ? '…' : (isApprove ? 'Approva' : 'Rifiuta')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function LeaveRequestsPage() {
  const { user } = useAuth();
  const { has } = usePermissions();
  const canApprove = has('leave.approve');
  const canViewAll = has('leave.view_all');
  // Chiunque sia autenticato può creare una richiesta PER SÉ — non c'è motivo di
  // gating: il diritto a chiedere ferie/permesso è universale per il dipendente.
  // Per terzi (OPS/AM/HR che creano per altri) la gating la fa il backend.
  const canCreate = true;
  const isOps = user?.role === 'operations_manager' || user?.role === 'hr_manager';
  const isAM = user?.role === 'area_manager';
  const isSM = user?.role === 'store_manager';
  // canPickStaff: l'utente può scegliere PER QUALE staff sta creando la richiesta
  // (es. OPS crea per Mario, AM crea per uno dei suoi). Per i dipendenti Staff
  // (`store_user`) il picker non appare — creano sempre per sé.
  const canPickStaff = isOps || isAM || isSM;

  const [tab, setTab] = useState(canApprove ? 'pending' : 'mine');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [decisionTarget, setDecisionTarget] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [myStaffId, setMyStaffId] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [storesList, setStoresList] = useState([]);
  const [calFilter, setCalFilter] = useState(emptyCascade());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [reqsResp, countResp, meResp] = await Promise.all([
        leaveApi.list().then(r => r.data).catch(() => []),
        leaveApi.pendingCount().then(r => r.data).catch(() => ({ count: 0 })),
        // Carica il proprio staff_member linkato — affidabile anche al primo
        // accesso (prima che l'utente abbia creato richieste).
        staffApi.me().then(r => r.data).catch(() => null),
      ]);
      setRequests(reqsResp || []);
      setPendingCount(countResp?.count || 0);
      if (meResp?.staff_id) {
        setMyStaffId(meResp.staff_id);
      } else {
        // Fallback: se non c'è uno staff linkato ma l'utente ha già richieste,
        // estrai lo staff_id dalla prima richiesta che ha creato.
        const fromReq = reqsResp?.find(r => r.created_by === user?.user_id);
        if (fromReq) setMyStaffId(fromReq.staff_id);
      }
    } catch (e) {
      toast.error('Errore caricamento richieste');
    } finally { setLoading(false); }
  }, [user?.user_id]);

  // Fetch iniziale: questo useEffect è un pattern standard di data fetching
  // (load() esegue richieste async + setState quando arrivano i risultati).
  useEffect(() => { load(); }, [load]);

  // Per pickup staff su "Nuova richiesta" — visibile a OPS/HR/AM/SM
  useEffect(() => {
    if (canPickStaff) {
      staffApi.list({ include_terminated: false }).then(r => {
        const arr = Array.isArray(r.data) ? r.data : [];
        setStaffList(arr.filter(s => s.status !== 'terminated').sort((a, b) =>
          (a.last_name || '').localeCompare(b.last_name || '', 'it')
        ));
      }).catch(() => {});
    }
  }, [canPickStaff]);

  // Store visibili per il filtro a cascata Brand/Area/Negozio (approvatori)
  // e per i nomi negozio nei badge di sovrapposizione del calendario.
  useEffect(() => {
    if (canApprove || canViewAll) {
      storesApi.list().then(r => setStoresList(r.data || [])).catch(() => {});
    }
  }, [canApprove, canViewAll]);

  const storeNames = useMemo(
    () => Object.fromEntries(storesList.map(s => [s.store_id, s.display_name || s.name])),
    [storesList]);

  const allowedStoreIds = useMemo(
    () => computeAllowedStoreIds(storesList, calFilter),
    [storesList, calFilter]);

  const calendarRequests = useMemo(() => {
    let list = requests.filter(r => r.status === 'pending' || r.status === 'approved');
    if (allowedStoreIds) list = list.filter(r => allowedStoreIds.has(r.store_id));
    return list;
  }, [requests, allowedStoreIds]);

  const filtered = useMemo(() => {
    let list = requests;
    if (tab === 'pending') list = list.filter(r => r.status === 'pending');
    if (tab === 'mine') {
      // "Le mie" = create dal mio user_id OPPURE riferite al mio staff
      list = list.filter(r => r.created_by === user?.user_id || r.staff_id === myStaffId);
    } else if (allowedStoreIds) {
      // Filtro Brand/Area/Negozio applicato a "Da approvare" e "Tutte"
      list = list.filter(r => allowedStoreIds.has(r.store_id));
    }
    if (filterStatus) list = list.filter(r => r.status === filterStatus);
    if (filterType) list = list.filter(r => r.type === filterType);
    return list;
  }, [requests, tab, user?.user_id, myStaffId, filterStatus, filterType, allowedStoreIds]);

  async function handleCancel() {
    if (!confirmCancel) return;
    try {
      await leaveApi.cancel(confirmCancel.request_id);
      toast.success('Richiesta annullata');
      setConfirmCancel(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore annullamento');
    }
  }

  function handleDecide(req, decision) {
    setDecisionTarget({ req, decision });
  }

  // Barra filtri Brand/Area/Negozio condivisa tra i tab Da approvare / Tutte / Calendario.
  const cascadeBlock = canApprove && storesList.length > 1 && (
    <StoreCascadeFilter stores={storesList} sel={calFilter} onChange={setCalFilter} testPrefix="leave-cal" />
  );

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">Ferie e Permessi</h1>
          <p className="text-sm text-slate-500 mt-1">
            {canApprove
              ? `Gestisci le richieste degli staff${pendingCount > 0 ? ` · ${pendingCount} in attesa` : ''}`
              : 'Le tue richieste di ferie, permessi e assenze'}
          </p>
        </div>
        {canCreate && (
          <Button onClick={() => setShowNew(true)} size="sm" className="gap-1.5" data-testid="new-leave-btn">
            <Plus className="w-3.5 h-3.5" /> Nuova richiesta
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {canApprove && (
            <TabsTrigger value="pending" data-testid="tab-pending">
              Da approvare {pendingCount > 0 && <span className="ml-1.5 text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">{pendingCount}</span>}
            </TabsTrigger>
          )}
          {(canViewAll || canApprove) && (
            <TabsTrigger value="all" data-testid="tab-all">Tutte</TabsTrigger>
          )}
          {(canViewAll || canApprove) && (
            <TabsTrigger value="calendar" data-testid="tab-calendar">Calendario</TabsTrigger>
          )}
          <TabsTrigger value="mine" data-testid="tab-mine">Le mie</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-4 pt-4">
          {cascadeBlock}
          {renderList(filtered, loading)}
        </TabsContent>
        <TabsContent value="all" className="space-y-4 pt-4">
          {cascadeBlock}
          <div className="flex gap-2 flex-wrap mb-3">
            <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-40" data-testid="filter-status"><SelectValue placeholder="Stato" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                {Object.entries(STATUS_BADGE).map(([v, b]) => <SelectItem key={v} value={v}>{b.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterType || 'all'} onValueChange={(v) => setFilterType(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-40" data-testid="filter-type"><SelectValue placeholder="Tipo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i tipi</SelectItem>
                {Object.entries(TYPE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {renderList(filtered, loading)}
        </TabsContent>
        <TabsContent value="calendar" className="pt-4 space-y-4">
          {cascadeBlock}
          <LeaveCalendar
            requests={calendarRequests}
            canApprove={canApprove}
            onDecide={handleDecide}
            storeNames={storeNames}
          />
        </TabsContent>
        <TabsContent value="mine" className="space-y-4 pt-4">
          {renderList(filtered, loading)}
        </TabsContent>
      </Tabs>

      {showNew && (
        <NewRequestDialog
          staffList={staffList}
          defaultStaffId={myStaffId}
          canPickStaff={canPickStaff}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); load(); }}
        />
      )}
      {decisionTarget && (
        <DecisionDialog
          req={decisionTarget.req}
          decision={decisionTarget.decision}
          onClose={() => setDecisionTarget(null)}
          onDecided={() => { setDecisionTarget(null); load(); }}
        />
      )}
      <AlertDialog open={!!confirmCancel} onOpenChange={(o) => !o && setConfirmCancel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Annulla richiesta</AlertDialogTitle>
            <AlertDialogDescription>
              Vuoi davvero annullare questa richiesta di {TYPE_LABELS[confirmCancel?.type]} dal {confirmCancel?.start_date}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancel}>Sì, annulla</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

  function renderList(list, isLoading) {
    if (isLoading) {
      return <div className="py-12"><div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin mx-auto" /></div>;
    }
    if (!list.length) {
      return (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-8 md:p-12 text-center" data-testid="leave-empty">
          <Clock className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-600 font-medium">Nessuna richiesta</p>
          {tab === 'mine' && canCreate ? (
            <>
              <p className="text-slate-500 text-sm mt-1 mb-4">Non hai ancora richiesto ferie o permessi.</p>
              <Button
                onClick={() => setShowNew(true)}
                size="sm"
                className="gap-1.5"
                data-testid="leave-empty-create-btn"
              >
                <Plus className="w-3.5 h-3.5" /> Crea la prima richiesta
              </Button>
            </>
          ) : (
            <p className="text-slate-500 text-sm mt-1">Nessuna richiesta nel filtro corrente.</p>
          )}
        </div>
      );
    }
    return list.map(req => (
      <LeaveRequestRow
        key={req.request_id}
        req={req}
        isApprover={canApprove}
        isOwner={req.created_by === user?.user_id || req.staff_id === myStaffId}
        onDecide={handleDecide}
        onCancel={setConfirmCancel}
      />
    ));
  }
}
